import {
  AccountBalance,
  Apartment,
  AutoAwesome,
  Business,
  Campaign,
  Diamond,
  Email,
  Groups,
  Hotel,
  Image,
  People,
  ReceiptLong,
  Science,
  Search,
  Settings,
  ShoppingCart,
  Store,
  SupportAgent,
  TravelExplore,
  Work,
} from '@mui/icons-material';

import {
  ROLE_ADMIN_USER,
  ROLE_CRUISE_COORDINATOR,
  ROLE_CX_TEAM_LEADER,
  ROLE_EMPLOYEE_USER,
  ROLE_EXPERIENCES_COORDINATOR,
  ROLE_HERO_PLANNER_ADMIN,
  ROLE_HERO_PLANNER_VIEWER,
  ROLE_HOTEL_COORDINATOR,
  ROLE_ICS_STORE_TEAM,
  ROLE_LERE_ADMIN,
  ROLE_LERE_USER,
  ROLE_LUX_LOYALTY_ADMIN,
  ROLE_TOUR_COORDINATOR,
} from './roles';
import tenants from './tenant';

export const NAVIGATION_LINK_IDS = {
  ACCOMMODATION: 'accommodation',
  AGENCIES: 'agencies',
  ASK_RICHARD: 'ask-richard',
  BANNER_UPLOAD: 'banner-upload',
  BRAZE: 'braze',
  BUSINESS: 'business',
  CS_ANALYSIS: 'cs-analysis',
  CS_AND_SALES: 'cs-and-sales',
  CS_NEW_HIRE_QUIZ: 'cs-new-hire-quiz',
  COMMERCIAL_OPS: 'commercial-ops',
  CX_DASHBOARD: 'cx-dashboard',
  CX_TRAINING_ACCOUNTS: 'cx-training-accounts',
  CURATION: 'curation',
  EMAIL: 'email',
  FINANCE: 'finance',
  HERO_PLANNER: 'hero-planner',
  INVOICE_TOOLS: 'invoice-tools',
  LAB: 'lab',
  LERE: 'lere',
  MARKETING: 'marketing',
  PURCHASES: 'purchases',
  SEARCH: 'search',
  AGENT_ACTIONS_DASHBOARD: 'agent-actions-dashboard',
  SALES_PERFORMANCE_DASHBOARD: 'sales-performance-dashboard',
  SALE_OWNERSHIP_SWAPS: 'sale-ownership-swaps',
  SOCIETE: 'societe',
  SUPPORT_ASSISTANT: 'support-assistant',
  SYSTEM: 'system',
  TRANSLATION_CATALOG: 'translation-catalog',
  USERS: 'users',
  VENDORS: 'vendors',
} as const;

export const NAVIGATION_LINKS: Array<App.NavigationLink> = [
  {
    id: NAVIGATION_LINK_IDS.PURCHASES,
    href: '/purchases',
    label: 'Purchases',
    icon: ShoppingCart,
    requiredRoles: new Set([
      ROLE_ADMIN_USER,
      ROLE_EMPLOYEE_USER,
      ROLE_TOUR_COORDINATOR,
      ROLE_EXPERIENCES_COORDINATOR,
      ROLE_ICS_STORE_TEAM,
      ROLE_CRUISE_COORDINATOR,
    ]),
  },
  {
    id: NAVIGATION_LINK_IDS.USERS,
    href: '/',
    label: 'Users',
    icon: People,
    requiredRoles: new Set([
      ROLE_ADMIN_USER,
      ROLE_EMPLOYEE_USER,
      ROLE_TOUR_COORDINATOR,
      ROLE_EXPERIENCES_COORDINATOR,
      ROLE_ICS_STORE_TEAM,
      ROLE_CRUISE_COORDINATOR,
    ]),
  },
  {
    id: NAVIGATION_LINK_IDS.AGENCIES,
    href: '/agencies/agencies',
    label: 'Agencies',
    icon: Apartment,
    requiredRoles: new Set([
      ROLE_ADMIN_USER,
      ROLE_EMPLOYEE_USER,
      ROLE_EXPERIENCES_COORDINATOR,
      ROLE_ICS_STORE_TEAM,
      ROLE_TOUR_COORDINATOR,
    ]),
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  {
    id: NAVIGATION_LINK_IDS.BUSINESS,
    href: '/lebusiness',
    label: 'Business',
    icon: Business,
    requiredRoles: new Set([ROLE_EMPLOYEE_USER, ROLE_ADMIN_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.VENDORS,
    href: '/vendors',
    label: 'Vendors',
    icon: Store,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR]),
  },
  {
    id: NAVIGATION_LINK_IDS.ACCOMMODATION,
    href: '/accommodation/properties',
    label: 'Accommodation',
    icon: Hotel,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR]),
  },
  {
    id: NAVIGATION_LINK_IDS.MARKETING,
    href: '/marketing',
    label: 'Marketing',
    icon: Campaign,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.HERO_PLANNER,
    href: '/customer-communication/hero-planner/schedules',
    label: 'Hero Planner',
    icon: AutoAwesome,
    requiredRoles: new Set([ROLE_HERO_PLANNER_VIEWER, ROLE_HERO_PLANNER_ADMIN]),
  },
  {
    id: NAVIGATION_LINK_IDS.BRAZE,
    label: 'Braze Upload',
    icon: Image,
    requiredRoles: new Set([ROLE_HERO_PLANNER_VIEWER, ROLE_HERO_PLANNER_ADMIN]),
    subLinks: [
      {
        id: NAVIGATION_LINK_IDS.BANNER_UPLOAD,
        href: '/customer-communication/banner-upload',
        label: 'Banner Upload',
        requiredRoles: new Set([ROLE_HERO_PLANNER_ADMIN]),
      },
      {
        id: NAVIGATION_LINK_IDS.TRANSLATION_CATALOG,
        href: '/customer-communication/translation-catalog',
        label: 'Translation Catalog',
        requiredRoles: new Set([ROLE_HERO_PLANNER_VIEWER, ROLE_HERO_PLANNER_ADMIN]),
      },
    ],
  },
  {
    id: NAVIGATION_LINK_IDS.EMAIL,
    href: '/email',
    label: 'Email',
    icon: Email,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.CURATION,
    href: '/curation',
    label: 'Curation',
    icon: TravelExplore,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.SEARCH,
    href: '/search',
    label: 'Search',
    icon: Search,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.FINANCE,
    href: '/finance',
    label: 'Finance',
    icon: AccountBalance,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.LAB,
    href: '/lab',
    label: 'Lab',
    icon: Science,
    requiredRoles: new Set([ROLE_ADMIN_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.COMMERCIAL_OPS,
    href: '/commercial-ops',
    label: 'Commercial Ops',
    icon: Work,
    requiredRoles: new Set([ROLE_EMPLOYEE_USER, ROLE_HOTEL_COORDINATOR]),
  },
  {
    id: NAVIGATION_LINK_IDS.SOCIETE,
    href: '/loyalty',
    label: 'Loyalty',
    icon: Diamond,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN]),
  },
  {
    id: NAVIGATION_LINK_IDS.LERE,
    href: '/lere',
    label: 'LERE',
    icon: Groups,
    requiredRoles: new Set([ROLE_LERE_ADMIN, ROLE_LERE_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.CS_AND_SALES,
    label: 'CS and Sales',
    icon: SupportAgent,
    requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_CX_TEAM_LEADER]),
    subLinks: [
      {
        id: NAVIGATION_LINK_IDS.SALES_PERFORMANCE_DASHBOARD,
        href: '/sales-performance-dashboard',
        label: 'Sales Performance Dashboard',
        requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
        section: 'Sales',
      },
      {
        id: NAVIGATION_LINK_IDS.SALE_OWNERSHIP_SWAPS,
        href: '/system/sale-ownership-swaps',
        label: 'Sale Ownership Swaps',
        requiredRoles: new Set([ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_CX_TEAM_LEADER]),
        section: 'Sales',
      },
      {
        id: NAVIGATION_LINK_IDS.SUPPORT_ASSISTANT,
        href: '/support-assistant/chats-history',
        label: 'Support Assignment',
        requiredRoles: new Set([ROLE_ADMIN_USER]),
        section: 'Customer Service',
      },
      {
        id: NAVIGATION_LINK_IDS.CX_DASHBOARD,
        href: '/cx-dashboard',
        label: 'At-Risk Tickets',
        requiredRoles: new Set([ROLE_EMPLOYEE_USER, ROLE_ADMIN_USER]),
        section: 'Customer Service',
      },
      {
        id: NAVIGATION_LINK_IDS.CX_TRAINING_ACCOUNTS,
        href: '/system/training-account-provisioning',
        label: 'Provision Training Accounts',
        requiredRoles: new Set([ROLE_ADMIN_USER]),
        enabledInProd: false,
        section: 'Customer Service',
      },
      {
        id: NAVIGATION_LINK_IDS.CS_NEW_HIRE_QUIZ,
        href: '/system/cs-new-hire-quiz',
        label: 'New Hire Quiz',
        requiredRoles: new Set([ROLE_ADMIN_USER]),
        enabledInProd: false,
        section: 'Customer Service',
      },
      {
        id: NAVIGATION_LINK_IDS.CS_ANALYSIS,
        href: '/system/cs-analysis',
        label: 'Transcript Analysis',
        requiredRoles: new Set([ROLE_ADMIN_USER]),
        section: 'Customer Service',
      },
      {
        id: NAVIGATION_LINK_IDS.AGENT_ACTIONS_DASHBOARD,
        href: '/system/cs-agent-actions-dashboard',
        label: 'Agent Actions Dashboard',
        requiredRoles: new Set([ROLE_ADMIN_USER]),
        section: 'Customer Service',
      },
    ],
  },
  {
    id: NAVIGATION_LINK_IDS.SYSTEM,
    href: '/system',
    label: 'System',
    icon: Settings,
    requiredRoles: new Set([ROLE_ADMIN_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.INVOICE_TOOLS,
    href: '/invoice-tools',
    label: 'Invoice Tools',
    icon: ReceiptLong,
    requiredRoles: new Set([ROLE_EMPLOYEE_USER, ROLE_ADMIN_USER]),
  },
  {
    id: NAVIGATION_LINK_IDS.ASK_RICHARD,
    href: '/ask-richard',
    label: 'askRichard',
    icon: AutoAwesome,
    requiredRoles: new Set([ROLE_EMPLOYEE_USER, ROLE_ADMIN_USER]),
  },
];
